import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import DepositAuditModal from './DepositAuditModal';
import DepositDetailModal from './DepositDetailModal';
import DepositUpdateAmountModal from './DepositUpdateAmountModal';
import DepositUpdateRateModal from './DepositUpdateRateModal';
import {
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  OFFLINE_DEPOSIT_STATUS_OPTIONS,
  buildOfflineDepositPageParams,
  canEditOfflineDeposit,
  defaultOfflineDepositApplyTimeRange,
  depositStatusClassName,
  formatAmount,
  formatDepositStatus,
  formatOptionalId,
  shouldShowDepositAuditTime,
  validateOfflineDepositTimeRange,
} from './depositConstants';

const DEFAULT_RANGE = defaultOfflineDepositApplyTimeRange();

const EMPTY_FILTERS = {
  id: '',
  status: '',
  roleId: '',
  topInviterId: '',
  inviterId: '',
  memberId: '',
  phoneEmail: '',
  currencyName: '',
  address: '',
  createTimeFromInput: DEFAULT_RANGE.createTimeFromInput,
  createTimeToInput: DEFAULT_RANGE.createTimeToInput,
};

const TABLE_COLUMNS = [
  '頂級上級用戶ID',
  '充值單號',
  '訂單流水號',
  '審核狀態',
  '帳號類型',
  '直屬上級用戶ID',
  '用戶ID',
  '充值幣種',
  '充值數量',
  '實際入款',
  '充值地址',
  '申請時間',
  '審核時間',
  '後台處理人',
  '備註',
  '操作',
];

const EMPTY_TOTAL = {
  recordCount: 0,
  amount: 0,
  realAmount: 0,
};

function statusCode(value) {
  if (!value) {
    return '';
  }
  return typeof value === 'string' ? value : value?.name || value?.code || String(value);
}

export default function DepositListPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(EMPTY_TOTAL);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [auditRow, setAuditRow] = useState(null);
  const [amountRow, setAmountRow] = useState(null);
  const [rateRow, setRateRow] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    const params = buildOfflineDepositPageParams(pageNo, nextFilters);
    try {
      const [pageData, totalData] = await Promise.all([
        adminApi.offlineDepositPage(params),
        adminApi.offlineDepositTotal(params),
      ]);
      setRecords(pageData?.records || []);
      setPage(pageData?.current || pageNo);
      setTotalPages(pageData?.pages || 1);
      setTotal(totalData || EMPTY_TOTAL);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
      setTotal(EMPTY_TOTAL);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function validateFilters() {
    return validateOfflineDepositTimeRange(
      filters.createTimeFromInput,
      filters.createTimeToInput,
      '申請時間'
    );
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateFilters();
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setError('');
    loadRecords(1, reset);
  }

  async function handleExport(exportCurrentPageOnly) {
    const rangeError = validateFilters();
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setExporting(true);
    setError('');
    try {
      await adminApi.exportOfflineDepositCsvZip({
        ...buildOfflineDepositPageParams(page, filters),
        exportCurrentPageOnly,
        fileName: 'member_offline_deposit',
      });
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  async function handleOpenAudit(row) {
    if (!row?.id) {
      return;
    }
    setActionId(row.id);
    setError('');
    try {
      const code = statusCode(row.status);
      if (code === 'PENDING') {
        await adminApi.depositOfflineLock({ id: row.id });
        await loadRecords(page, filters);
        const refreshed = (
          await adminApi.offlineDepositPage(buildOfflineDepositPageParams(page, filters))
        )?.records?.find((item) => item.id === row.id);
        setAuditRow(refreshed || { ...row, status: 'PROCESSING' });
      } else {
        setAuditRow(row);
      }
    } catch (err) {
      setError(err.message || '接單失敗，無法審核');
    } finally {
      setActionId(null);
    }
  }

  return (
    <div className="page-card">
      <h2>充值列表</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="dpId">充值單號</label>
          <input
            id="dpId"
            type="number"
            value={filters.id}
            onChange={(e) => updateFilter('id', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="dpStatus">審核狀態</label>
          <select
            id="dpStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="">全部</option>
            {OFFLINE_DEPOSIT_STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="dpRoleId">帳號類型</label>
          <select
            id="dpRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="dpTopInviterId">頂級上級用戶ID</label>
          <input
            id="dpTopInviterId"
            type="number"
            value={filters.topInviterId}
            onChange={(e) => updateFilter('topInviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="dpInviterId">直屬上級用戶ID</label>
          <input
            id="dpInviterId"
            type="number"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="dpMemberId">用戶ID</label>
          <input
            id="dpMemberId"
            type="number"
            value={filters.memberId}
            onChange={(e) => updateFilter('memberId', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="dpPhoneEmail">郵箱/手機</label>
          <input
            id="dpPhoneEmail"
            type="text"
            value={filters.phoneEmail}
            onChange={(e) => updateFilter('phoneEmail', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="dpCurrencyName">充值幣種</label>
          <input
            id="dpCurrencyName"
            type="text"
            value={filters.currencyName}
            onChange={(e) => updateFilter('currencyName', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="dpAddress">充值地址</label>
          <input
            id="dpAddress"
            type="text"
            value={filters.address}
            onChange={(e) => updateFilter('address', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="dpCreateTimeFrom">申請時間起</label>
          <input
            id="dpCreateTimeFrom"
            type="datetime-local"
            value={filters.createTimeFromInput}
            onChange={(e) => updateFilter('createTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="dpCreateTimeTo">申請時間迄</label>
          <input
            id="dpCreateTimeTo"
            type="datetime-local"
            value={filters.createTimeToInput}
            onChange={(e) => updateFilter('createTimeToInput', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（全部資料）'}
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const mainStatus = statusCode(row.status);

                return (
                  <tr key={row.id}>
                    <td>{formatOptionalId(row.topInviterId)}</td>
                    <td className="mono-cell">{formatOptionalId(row.id)}</td>
                    <td className="mono-cell">{row.txId || '—'}</td>
                    <td className={depositStatusClassName(mainStatus)}>
                      {formatDepositStatus(mainStatus)}
                    </td>
                    <td>{MEMBER_ROLE_LABELS[row.roleId] || row.roleId || '—'}</td>
                    <td>{formatOptionalId(row.inviterId)}</td>
                    <td>{formatOptionalId(row.memberId)}</td>
                    <td>{row.currencyName || '—'}</td>
                    <td>{formatAmount(row.amount)}</td>
                    <td>{formatAmount(row.realAmount)}</td>
                    <td className="mono-cell remark-cell">{row.address || '—'}</td>
                    <td>{formatDateTime(row.createTime)}</td>
                    <td>
                      {shouldShowDepositAuditTime(row) ? formatDateTime(row.updateTime) : '—'}
                    </td>
                    <td>{formatOptionalId(row.adminId)}</td>
                    <td className="remark-cell">{row.remark || '—'}</td>
                    <td>
                      <div className="table-actions table-actions--stack">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setDetailRow(row)}
                        >
                          詳情
                        </button>
                        {canEditOfflineDeposit(row) && (
                          <>
                            <button
                              type="button"
                              className="btn btn-success btn-sm"
                              disabled={actionId === row.id}
                              onClick={() => handleOpenAudit(row)}
                            >
                              {actionId === row.id ? '處理中…' : '更新審核狀態'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setAmountRow(row)}
                            >
                              更改充值數量
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setRateRow(row)}
                            >
                              更改匯率
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
            {!loading && records.length > 0 && (
              <tr className="summary-row">
                <td colSpan={8}>合計 {total.recordCount ?? 0} 筆</td>
                <td>{formatAmount(total.amount)}</td>
                <td>{formatAmount(total.realAmount)}</td>
                <td colSpan={6} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <DepositDetailModal
        open={Boolean(detailRow)}
        row={detailRow}
        onClose={() => setDetailRow(null)}
      />

      <DepositAuditModal
        open={Boolean(auditRow)}
        row={auditRow}
        onClose={() => setAuditRow(null)}
        onSaved={() => loadRecords(page, filters)}
      />

      <DepositUpdateAmountModal
        open={Boolean(amountRow)}
        row={amountRow}
        onClose={() => setAmountRow(null)}
        onSaved={() => loadRecords(page, filters)}
      />

      <DepositUpdateRateModal
        open={Boolean(rateRow)}
        row={rateRow}
        onClose={() => setRateRow(null)}
        onSaved={() => loadRecords(page, filters)}
      />
    </div>
  );
}
